/**
 * Common personal / free email domains that should be blocked
 * when requiring a work email address.
 */
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.in',
  'yahoo.ca',
  'yahoo.com.au',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'live.co.uk',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'yandex.ru',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
  'inbox.com',
  'mail.ru',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'cox.net',
  'charter.net',
  'earthlink.net',
  'optonline.net',
  'rocketmail.com',
];

/**
 * Check if an email address looks like a valid format.
 */
export function isValidEmailFormat(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Check if an email address uses a personal/free email domain.
 * Returns the domain name if blocked, or null if allowed.
 */
export function getBlockedEmailDomain(email: string): string | null {
  const domain = email.trim().toLowerCase().split('@')[1];
  if (!domain) return null;
  return BLOCKED_EMAIL_DOMAINS.includes(domain) ? domain : null;
}

/**
 * Validate a work email field. Returns an error message or empty string if valid.
 */
export function validateWorkEmail(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return 'Work email is required';
  if (!isValidEmailFormat(trimmed)) return 'Please enter a valid email address';
  const blocked = getBlockedEmailDomain(trimmed);
  if (blocked) return `Please use your work email address. ${blocked} is not accepted.`;
  return '';
}

/**
 * Validate a phone number (optional, but if provided should be reasonable).
 */
export function validatePhone(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return ''; // phone is optional
  // Allow digits, spaces, dashes, parentheses, plus sign — at least 7 digits
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length < 7) return 'Please enter a valid phone number';
  if (digitsOnly.length > 15) return 'Phone number is too long';
  return '';
}
